import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Check, ShieldCheck } from "@/lib/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/marketing/reveal";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";
import { SectionHeading } from "@/components/marketing/site/section-heading";
import { AriaDemoSection } from "@/components/marketing/site/live-demos";
import { SarahDemoSection } from "@/components/marketing/site/sarah-demo-section";
import { NancyRunSection } from "@/components/marketing/proof/nancy-run";
import { AiShowcaseCards, goToAiDemo } from "@/components/marketing/site/experience/ai-showcase";
import {
  DemoSwitcher,
  workflowFromHash,
} from "@/components/marketing/site/experience/workflow-switcher";
import { WorkflowCards } from "@/components/marketing/site/experience/workflow-cards";
import { type WorkflowId } from "@/components/marketing/site/experience/workflow-switcher-types";
import { WorkflowDemos } from "@/components/marketing/site/experience/workflow-demos";
import { marketingHead } from "@/lib/marketing/seo";
import { breadcrumbs, webPageOf } from "@/lib/marketing/ppl-schema";
import { MLink } from "@/lib/marketing/m-link";

/** Try it live: the staff first (Aria and Sarah answering over a real connection, and a recorded
 * Nancy purchasing run), then the four software workflows on sample data as the operating system
 * they write to. */

/* The employee sections sit under a second sticky bar, so an anchor has further to clear than the
 * 80px these sections allow for everywhere else: the header is 54px and the strip ends at 120.
 * Without it the strip sits on the first line of whichever employee a visitor just asked for. */
const BELOW_THE_STRIP = "scroll-mt-32";

/** The terms of entry, stated before the first demo rather than discovered. Honest for the
 * employees as well as the software: Aria asks for an email and Sarah for a microphone, and each
 * card says so, but none of it needs an account or reaches a real record. */
const OPEN_TERMS = [
  "No account",
  "Nothing to install",
  "A community we made up",
  "Nothing you do reaches a real record",
];

function MarketingExperience() {
  /* One workflow at a time. The four sections stay mounted and hidden, so
     switching away and back keeps a half-run pass where it was; the hash names
     the open one so a link to #med-pass still lands on the med pass. */
  const [active, setActive] = useState<WorkflowId>("crm");
  useEffect(() => {
    /* Only a workflow's own hash changes the open workflow. The strip also links to #aria-demo,
     * #talk-to-sarah and #nancy-run, and treating those as "no workflow named" would snap a
     * half-run med pass back to the pipeline every time a visitor went to meet an employee. */
    const onHash = () => {
      const named = workflowFromHash();
      if (named) setActive(named);
    };
    onHash();
    window.addEventListener("hashchange", onHash);
    return () => {
      window.removeEventListener("hashchange", onHash);
    };
  }, []);

  /* A `/experience#med-pass` link cannot jump on its own: the pipeline renders open server-side, so
   * the med pass is `hidden` at first paint and has no position. Once the effect opens it, scroll
   * there. */
  useEffect(() => {
    if (workflowFromHash() !== active) return;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(active)?.scrollIntoView({ behavior: "auto", block: "start" });
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [active]);

  const select = (id: WorkflowId) => {
    /* Flushed, so the chosen section is visible before it is scrolled to. It
       is `hidden` until the state lands, and a hidden element scrolls nowhere;
       its scroll margin clears the strip. */
    flushSync(() => {
      setActive(id);
    });
    window.history.replaceState(null, "", `#${id}`);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <MarketingShell>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="relative mx-auto max-w-6xl px-4 pt-16 pb-10 text-center md:pt-28 md:pb-14">
          <Reveal>
            <Badge
              variant="outline"
              className="border-primary/30 bg-background/60 text-foreground/80 mb-6 gap-1.5 px-3 py-1 backdrop-blur-sm"
              data-testid="badge-experience"
            >
              Three AI employees. Two of them answer you live.
            </Badge>
          </Reveal>
          <Reveal delay={60}>
            <h1
              className="font-display mx-auto max-w-4xl text-4xl leading-[1.05] font-semibold tracking-tight text-balance md:text-6xl"
              data-testid="text-experience-title"
            >
              Put an AI employee to work, <span className="text-gradient-warm">right now</span>
            </h1>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-foreground/80 mx-auto mt-6 max-w-2xl text-lg text-pretty">
              Aria, Sarah and Nancy are three of PEOPLE AI Workforce's AI workforce, and this is
              where you try them. Ask Aria a question in plain English and she builds the screen
              that answers it. Ring Sarah and she picks up, out loud, over the same line a caller
              hears. Nancy's is a real week of purchasing, recorded, stepped through at your own
              pace. Not a slideshow and not a video: the employees, working.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <p className="text-foreground/75 mx-auto mt-4 hidden max-w-2xl text-pretty sm:block">
              Under them, four of the workflows they work in, loaded with sample data and built from
              the same components the product ships: a morning med pass, a move-in, the lead
              pipeline and a month of invoices. Everything on this page is clickable, go ahead.
            </p>
          </Reveal>
          <Reveal delay={180}>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              <Button asChild size="lg" className="font-ui">
                <a
                  href="#aria-demo"
                  onClick={(event) => {
                    goToAiDemo(event, "aria-demo");
                  }}
                  data-testid="link-experience-aria"
                >
                  Ask Aria
                  <ArrowRight className="ml-2 size-4" />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="bg-background/60 font-ui backdrop-blur-sm"
              >
                <a
                  href="#talk-to-sarah"
                  onClick={(event) => {
                    goToAiDemo(event, "talk-to-sarah");
                  }}
                  data-testid="link-experience-sarah"
                >
                  Call Sarah
                </a>
              </Button>
            </div>
          </Reveal>
          <Reveal delay={210}>
            <ul className="text-muted-foreground mx-auto mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs">
              {OPEN_TERMS.map((term) => (
                <li key={term} className="inline-flex items-center gap-1.5">
                  <Check className="text-primary size-3.5 shrink-0" />
                  {term}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={240}>
            <p className="text-muted-foreground mx-auto mt-3 flex items-center justify-center gap-1.5 text-xs">
              <ShieldCheck className="text-primary size-3.5" />
              Aria asks for an email so we know who tried her, and Sarah for your microphone. That
              is all either wants.
            </p>
          </Reveal>
          <Reveal delay={270}>
            {/* Shown on a phone too: the strip carries the employees by name
                only, and a name is not a reason to tap. */}
            <p className="text-muted-foreground mt-10 text-sm font-medium">Meet them</p>
            <div className="mt-4">
              <AiShowcaseCards />
            </div>
          </Reveal>
          <Reveal delay={300}>
            {/* Hidden on a phone: the strip below carries the same four
                choices, and on a small screen the cards were a screen of their
                own before the first employee. */}
            <div className="hidden sm:block">
              <p className="text-muted-foreground mt-10 text-sm font-medium">
                And the software they work in
              </p>
              <WorkflowCards onSelect={select} />
            </div>
          </Reveal>
        </div>
      </section>

      <DemoSwitcher active={active} onSelect={select} />

      {/* No sample data: a real agent answering over a real connection, then a recorded run. Each
          one's terms (an email, a microphone) show on its hero card and again on the section, so
          the ask comes before the control. */}
      <AriaDemoSection
        className={BELOW_THE_STRIP}
        id="aria-demo"
        kicker="Live, Aria"
        title="Ask a question that takes four screens to answer"
        lede="Every workflow further down this page shows you one screen. Aria works all of them at once, census, incidents, care plans, the enquiry pipeline, reading the whole community and writing back to it, and answering whatever you type by building the interface the answer needs. The same renderer a nurse sees, an invented community underneath, and writes switched off for this session."
      />

      <SarahDemoSection
        className={BELOW_THE_STRIP}
        id="talk-to-sarah"
        kicker="Live, Sarah"
        title="Talk to the receptionist out loud"
        lede="Not a transcript and not a recording. Press call and Sarah picks up, over the same telephone-quality audio a caller on a real phone hears. Ask for somebody by name, then stay on the line for the part that matters, what she does when they cannot take it."
      />

      {/* Recorded, and labelled so — see NancyRunSection for why a stranger cannot start one. A
          page about trying the employees that showed two of the three with something to show would
          be leaving out the one with a number attached. */}
      <NancyRunSection className={BELOW_THE_STRIP} />

      <WorkflowDemos active={active} />

      {/* Bridge to signup */}
      <section className="bg-card/40 border-t border-border/60">
        <div className="mx-auto max-w-6xl px-4 py-16 md:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="And this is the small version"
              title="The real thing goes much deeper"
              subtitle="Three employees and four workflows are the miniature. The full workforce is ten, and the platform they work in adds therapy EMR, staff scheduling and handoff boards, fax and SMS inboxes, family and physician portals, hiring, training and more."
            />
          </Reveal>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Button size="lg" className="font-ui" data-testid="button-experience-demo" asChild>
              <MLink href="/demo">
                Book a live demo
                <ArrowRight className="ml-2 size-4" />
              </MLink>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-background/60 font-ui backdrop-blur-sm"
              data-testid="button-experience-workforce"
              asChild
            >
              <MLink href="/workforce">Meet the whole workforce</MLink>
            </Button>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export const Route = createFileRoute("/v2/experience")({
  component: MarketingExperience,
  head: () => {
    const seo = {
      title:
        "Try the AI employees live: ask Aria, call Sarah, watch Nancy work | PEOPLE AI Workforce",
      description:
        "Ask Aria a question and she builds the screen that answers it. Call Sarah and she picks up, out loud. Step through a real purchasing run of Nancy's. Three of PEOPLE AI Workforce's AI employees, live in your browser, with four of the workflows they work in underneath. No account.",
      path: "/v2/experience",
      imageAlt: "PEOPLE AI Workforce. The AI workforce for assisted living communities",
    };
    return marketingHead({
      ...seo,
      schema: [webPageOf(seo), breadcrumbs({ name: "Try it live", path: "/experience" })],
    });
  },
});
